import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    Plus,
    Search,
    Filter,
    Pencil,
    Trash2,
    Copy,
    ArrowLeft,
    Layers,
    Clock,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkoutsList() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner" && user.role !== "coach") navigate("/dashboard", { replace: true });
    }, [loading, user, navigate]);

    const refresh = async () => {
        setBusy(true);
        try {
            const params = {};
            if (category) params.category = category;
            if (q.trim()) params.q = q.trim();
            const r = await axios.get(`${API}/coach/workouts`, { params, withCredentials: true });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load workouts", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (user?.role === "owner" || user?.role === "coach") refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, category]);

    const remove = async (w) => {
        if (!window.confirm(`Delete "${w.title}"?`)) return;
        try {
            await axios.delete(`${API}/coach/workouts/${w.workout_id}`, { withCredentials: true });
            toast.success("Deleted");
            refresh();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        }
    };

    const duplicate = async (w) => {
        try {
            const r = await axios.post(
                `${API}/coach/workouts/${w.workout_id}/duplicate`,
                {}, { withCredentials: true },
            );
            toast.success("Duplicated", { description: r.data?.title });
            navigate(`/coach/workouts/${r.data.workout_id}/edit`);
        } catch (e) {
            toast.error("Couldn't duplicate", { description: e?.response?.data?.detail });
        }
    };

    if (loading || !user) return null;

    return (
        <div data-testid="workouts-list" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Link to="/coach" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </Link>
                    <div className="grid md:grid-cols-12 gap-6 items-end">
                        <div className="md:col-span-8">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" /> Programming
                            </div>
                            <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                                WORKOUT <span className="text-brand-red">LIBRARY.</span>
                            </h1>
                            <p className="mt-4 text-zinc-200 max-w-2xl">
                                Author once. Assign to athletes. Every set carries a load percentage, rep target, and rest interval — anchored to the 5S framework.
                            </p>
                        </div>
                        <div className="md:col-span-4 md:text-right space-y-2">
                            <Link to="/coach/exercises" className="btn-ghost-tactical inline-flex">Exercise library</Link>
                            <div>
                                <Link to="/coach/workouts/new" data-testid="workout-new" className="btn-tactical inline-flex">
                                    <Plus className="w-4 h-4" /> New workout
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-5 border-b border-zinc-900 sticky top-16 md:top-20 bg-brand-obsidian/95 backdrop-blur z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row gap-4 md:items-center justify-between">
                    <form onSubmit={(e) => { e.preventDefault(); refresh(); }} className="flex items-center gap-2 flex-1 max-w-md">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search workouts…"
                                className="w-full bg-black border border-zinc-800 pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red"
                            />
                        </div>
                        <button type="submit" className="btn-ghost-tactical">Go</button>
                    </form>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <Filter className="w-3.5 h-3.5 text-zinc-300 mr-1" />
                        <button onClick={() => setCategory("")} className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 ${category === "" ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200 hover:text-white"}`}>All</button>
                        {CATEGORIES.map((c) => (
                            <button key={c.value} data-testid={`workouts-filter-${c.value}`} onClick={() => setCategory(c.value)} className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 ${category === c.value ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200 hover:text-white"}`}>{c.label}</button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    {busy ? (
                        <div className="py-16 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950 p-10 text-center">
                            <Layers className="w-7 h-7 text-zinc-200 mx-auto mb-3" />
                            <div className="font-display text-2xl tracking-wider text-white">No workouts yet</div>
                            <div className="text-sm text-zinc-300 mt-2">Build your first one — start with a strength block.</div>
                            <Link to="/coach/workouts/new" className="btn-tactical mt-5 inline-flex">
                                <Plus className="w-4 h-4" /> New workout
                            </Link>
                        </div>
                    ) : (
                        <div className="border border-zinc-900">
                            {rows.map((w) => {
                                const blockCount = (w.blocks || []).length;
                                const exerciseCount = (w.blocks || []).reduce((acc, b) => acc + (b.levels?.[0]?.sets_data?.length ? 1 : 1) * (b.levels?.length ? 1 : 0), 0);
                                return (
                                    <Link
                                        key={w.workout_id}
                                        to={`/coach/workouts/${w.workout_id}/edit`}
                                        data-testid={`workout-row-${w.workout_id}`}
                                        className="px-5 py-4 flex items-center justify-between gap-4 border-b border-zinc-900 last:border-b-0 bg-zinc-950 hover:bg-zinc-900/40 transition-colors group"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-[10px] tracking-[0.25em] uppercase text-brand-red">{CATEGORY_LABELS[w.category]}</span>
                                                {w.subtabs?.length > 0 && (
                                                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">· {w.subtabs.join(" · ")}</span>
                                                )}
                                            </div>
                                            <div className="font-display text-2xl tracking-wider text-white group-hover:text-brand-red transition-colors">{w.title}</div>
                                            <div className="text-xs text-zinc-300 flex items-center gap-3 mt-1.5">
                                                <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {blockCount} block{blockCount === 1 ? "" : "s"}</span>
                                                {w.estimated_duration && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {w.estimated_duration} min</span>}
                                                {!!exerciseCount && <span>{exerciseCount} level{exerciseCount === 1 ? "" : "s"}</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <button
                                                type="button"
                                                data-testid={`duplicate-workout-${w.workout_id}`}
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); duplicate(w); }}
                                                className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                            >
                                                <Copy className="w-3.5 h-3.5" /> Duplicate
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(w); }}
                                                className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" /> Delete
                                            </button>
                                            <Pencil className="w-4 h-4 text-zinc-200 group-hover:text-brand-red transition-colors" />
                                            <ChevronRight className="w-4 h-4 text-zinc-200" />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
